import React, { useEffect, useState } from 'react';
import { ButcherHeader } from '@components/ButcherHeader';
import { OrderBreadcrumb } from '@components/OrderCreation/OrderBreadcrumbs';
import { OrderProfileCard } from '@components/OrderCreation/OrderProfileCard';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { OrderStepper } from '@components/OrderCreation/OrderStepper';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  errorMessage,
  orderBreadCrumbSteps,
  OrderCreationMasterData,
  orderStepper,
  ProductType,
  responseTexts,
  WeightType,
} from '@helpers/Constants';
import { PackagingBottomSheet } from '@components/BottomSheet/PackagingBottomSheet';
import {
  AddonsListProps,
  MasterPackage,
  MasterProduct,
  NewOrderInitiationProps,
  ProductEntry,
  ProductPackageCustomization,
} from '@helpers/Models';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { useNavigate } from 'react-router-dom';
import { CustomerOrderService } from '@services/CustomerService';
import { useToast } from '@contexts/Toast';
import { Axios } from '@helpers/ApiService';

export const OrderPackaging: React.FC = () => {
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [activeCardIndex, setActiveCardIndex] = useState<number | null>(null);
  const [activePackageIndex, setActivePackageIndex] = useState<number | null>(
    null,
  );
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const [customerId, setCustomerId] = useState<number>(0);
  const [currentCustomizingId, setCurrentCustomizingId] = useState<string>('');
  const [masterProducts, setMasterProducts] = useState<MasterProduct[]>([]);
  const [masterPackages, setMasterPackages] = useState<MasterPackage[]>([]);
  const [addons, setAddons] = useState<AddonsListProps[]>([]);
  const [productCustomizations, setProductCustomizations] = useState<
    ProductPackageCustomization[]
  >([]);
  const [customerDetails, setCustomerDetails] =
    useState<NewOrderInitiationProps>();

  /**
   * Effect hook to initialize customer and customizing IDs on component mount.
   *
   * - Retrieves the current customer ID via `CustomerOrderService.getCustomerId()`.
   *   - If found, sets the customer ID state.
   *   - Then retrieves the current customizing ID via `CustomerOrderService.getcurrentCustomizingId()`.
   *     - If found, sets the customizing ID state.
   * - If no customer ID is found, navigates to the customer list page.
   *
   * Runs only once when the component mounts.
   */
  useEffect(() => {
    const customerID = CustomerOrderService.getCustomerId();
    const customerDetails = CustomerOrderService.getCustomerDetails();
    setCustomerDetails(customerDetails);
    if (customerID) {
      setCustomerId(customerID);
      const currentCustomizingId = localStorage.getItem('currentCustomizingId');
      if (currentCustomizingId) {
        setCurrentCustomizingId(currentCustomizingId);
      }
    } else {
      navigate(FrontendRoutes.CUSTOMER_LIST);
    }
  }, []);

  /**
   * Effect hook to fetch initial order-related data when `customerId` is set.
   *
   * When `customerId` changes (and is truthy), it triggers:
   * - `getOrderData()` to load existing order data for the customer.
   * - `fetchMasterProducts()` to retrieve master product list.
   * - `fetchMasterPackages()` to retrieve master packaging options.
   * - `fetchAddons()` to retrieve add-on options.
   *
   * Runs every time `customerId` updates.
   */
  useEffect(() => {
    if (customerId) {
      getOrderData();
      fetchMasterProducts();
      fetchMasterPackages();
      fetchAddons();
    }
  }, [customerId]);

  /**
   * Loads and sets existing order data for the current customer and customizing session.
   *
   * Retrieves parsed order data from CustomerOrderService and extracts the products
   * for the current customer and species index (`currentCustomizingId`).
   *
   * - Builds a `selected` map to track which product IDs are selected.
   * - Builds a `quantities` map to store quantity for each selected product.
   * - Updates the `productCustomizations` state with the retrieved products.
   *
   * Requires `customerId` and `currentCustomizingId` to be set.
   */
  const getOrderData = (): void => {
    const orderData = CustomerOrderService.getParsedOrderData();

    if (orderData && customerId) {
      const customerOrder = orderData[customerId];
      const existingData = customerOrder?.species?.[currentCustomizingId];
      // Tracks selected product IDs to control UI selection state
      const selected: Record<number, boolean> = {};
      // Stores quantity count for each product by product ID
      const quantities: Record<number, number> = {};

      if (existingData?.products && Array.isArray(existingData.products)) {
        console.log(existingData.products);
        existingData.products.forEach((product: ProductEntry) => {
          if (product.productId != null) {
            selected[product.productId] = true;
            quantities[product.productId] = product.quantity ?? 1;
          }
        });
        setProductCustomizations(existingData.products);
      }
    }
  };

  /**
   * Fetches master product data from the API.
   *
   * Makes a POST request to the `FETCH_SPECIES_MASTERDATA` endpoint with
   * `masterDataType` set to 4 to retrieve the species master products.
   *
   * On success, updates the `masterProducts` state with the fetched data.
   * On failure, shows an error toast message and throws the error.
   */
  const fetchMasterProducts = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PRODUCT_MASTER_DATA,
      });
      const masterData = response.data.data.masterData;
      setMasterProducts(masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches master packaging data from the API and sets default packaging.
   *
   * Makes a POST request to the `FETCH_SPECIES_MASTERDATA` endpoint with
   * `masterDataType` set to 6 to retrieve the packaging master data.
   *
   * On success, updates the `masterPackages` state with the `packageType` array
   * from the response data and auto-selects the first package as default for
   * products that don't have packaging yet.
   * On failure, shows an error toast message and rethrows the error.
   */
  const fetchMasterPackages = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PACKAGE_MASTER_DATA,
      });
      const masterData = response.data.data.masterData.packageType;
      setMasterPackages(masterData);

      const defaultPackage = masterData?.find(
        (masterPackage: { defaultFlag: number }) =>
          masterPackage.defaultFlag === 1,
      );

      // Auto-select default (first) package for products without packaging
      if (masterData && masterData.length > 0) {
        setProductCustomizations((prev) => {
          return prev.map((product) => {
            // If product has no packaging or empty packaging array, set default
            if (!product.packaging || product.packaging.length === 0) {
              return {
                ...product,
                packaging: [
                  {
                    productPackageTypeId: defaultPackage.id,
                    percentage: 100, // Default to 100% for single package
                  },
                ],
              };
            }
            return product;
          });
        });
      }
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches the list of addons from the API.
   *
   * Sends a POST request to the `FETCH_ADDONS` endpoint with an empty payload.
   * On success, updates the `addons` state with the received addon data.
   * On failure, displays an error toast message and rethrows the error.
   */
  const fetchAddons = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_ADDONS, {});
      setAddons(response.data.data.addOns);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Handles selection of a packaging option for the currently active product.
   *
   * Adds the selected packaging (with optional percentage) to the packaging array
   * of the product at the `activeCardIndex` in `productCustomizations`.
   * If no packaging array exists for the product, initializes it first.
   *
   * After adding, closes the packaging bottom sheet and resets the active card index.
   *
   * @param selectedPackaging - The selected packaging object containing:
   *    - productPackageTypeId: The ID of the selected packaging type.
   *    - percentage (optional): The percentage value for this packaging.
   */
  const handleSelectPackaging = (selectedPackaging: {
    productPackageTypeId: number;
    percentage?: number;
  }): void => {
    if (activeCardIndex === null) return;

    setProductCustomizations((prev) => {
      const updated = [...prev];
      const product = updated[activeCardIndex];

      if (!product.packaging) {
        product.packaging = [];
      }

      // If editing an existing package (activePackageIndex is not null)
      if (activePackageIndex !== null) {
        // Check if the new package type already exists in other positions
        const existsElsewhere = product.packaging.some(
          (packaging, idx) =>
            idx !== activePackageIndex &&
            packaging.productPackageTypeId ===
              selectedPackaging.productPackageTypeId,
        );

        if (existsElsewhere) {
          showToastMessage(errorMessage.ORDER_DUPLICATE_PACKAGE, 'error');
          return prev;
        }

        // Replace the existing package at activePackageIndex
        product.packaging[activePackageIndex] = {
          productPackageTypeId: selectedPackaging.productPackageTypeId,
          percentage:
            selectedPackaging.percentage ??
            product.packaging[activePackageIndex].percentage ??
            100,
        };
      } else {
        // Adding a new package
        const alreadyExists = product.packaging.some(
          (packaging) =>
            packaging.productPackageTypeId ===
            selectedPackaging.productPackageTypeId,
        );

        if (alreadyExists) {
          showToastMessage(errorMessage.ORDER_DUPLICATE_PACKAGE, 'error');
          return prev;
        }
        // Package percentage split up to 50 50 when new package is added
        if (product.packaging.length > 0) {
          // If there's an existing package, set its percentage to 50
          product.packaging[0] = {
            ...product.packaging[0],
            percentage: 50,
          };
          // Add the new package and set its percentage to 50
          product.packaging.push({
            productPackageTypeId: selectedPackaging.productPackageTypeId,
            percentage: 50,
          });
        } else {
          product.packaging.push({
            productPackageTypeId: selectedPackaging.productPackageTypeId,
            percentage: selectedPackaging.percentage ?? 0,
          });
        }
      }

      return updated;
    });

    // Closing the bottom sheet
    setShowBottomSheet(false);
    setActiveCardIndex(null);
    setActivePackageIndex(null);
  };

  /**
   * Removes a packaging entry from a specific product's packaging list.
   *
   * Creates a shallow copy of the products array and the targeted product,
   * then clones the packaging array to safely remove the packaging at `packagingIndex`.
   * Updates the product's packaging with the new array and updates the products array.
   *
   * @param productIndex - Index of the product in productCustomizations.
   * @param packagingIndex - Index of the packaging to remove from the product's packaging array.
   */
  const handleRemovePackaging = (
    productIndex: number,
    packagingIndex: number,
  ): void => {
    setProductCustomizations((prev) => {
      const updated = [...prev];
      const product = { ...updated[productIndex] };

      const packaging = product.packaging ? [...product.packaging] : [];
      packaging.splice(packagingIndex, 1);

      product.packaging = packaging;
      updated[productIndex] = product;

      return updated;
    });
  };

  /**
   * Updates the percentage value for a specific packaging item within a product.
   *
   * If the product has exactly two packaging items, changing the percentage of one
   * automatically adjusts the other to ensure the total percentage sums to 100.
   *
   * The percentage value is clamped between 0 and 100.
   *
   * @param productIndex - Index of the product in the productCustomizations array.
   * @param packagingIndex - Index of the packaging item within the product's packaging array.
   * @param newPercentage - The new percentage value to assign.
   */
  const handlePercentageChange = (
    productIndex: number,
    packagingIndex: number,
    newPercentage: number,
  ): void => {
    setProductCustomizations((prev) => {
      const updated = [...prev];
      const packagingList = updated[productIndex].packaging;

      if (!packagingList) return updated;
      const safePercentage = Math.max(0, Math.min(100, newPercentage));

      packagingList[packagingIndex].percentage = safePercentage;

      // If exactly two packagings, auto-fill the other one
      if (packagingList.length === 2) {
        const otherIndex = packagingIndex === 0 ? 1 : 0;
        const remaining = 100 - safePercentage;
        packagingList[otherIndex].percentage = remaining >= 0 ? remaining : 0;
      }
      return updated;
    });
  };

  /**
   * Validates the product customizations before proceeding.
   * Ensures each product has packaging and the packaging percentages sum to 100.
   * If validation passes, saves the data to localStorage and navigates to the next page.
   */
  const handleNext = (): void => {
    // Adding percentage as 100 when the one package is selected.
    const updatedProductCustomizations = productCustomizations.map(
      (product) => {
        if (product.packaging?.length === 1) {
          return {
            ...product,
            packaging: [
              {
                ...product.packaging[0],
                percentage: 100,
              },
            ],
          };
        }
        return product;
      },
    );

    // Check if any product is missing packaging
    const hasMissingPackaging = updatedProductCustomizations.some(
      (product) => !product.packaging || product.packaging.length === 0,
    );

    // Check if any product has invalid total percentage
    const hasInvalidPercentage = updatedProductCustomizations.some(
      (product) => {
        if (!product.packaging || product.packaging.length === 0) return false;
        const totalPercentage = product.packaging.reduce(
          (sum, p) => sum + (p.percentage ?? 0),
          0,
        );
        return totalPercentage !== 100;
      },
    );

    // Packaging required error
    if (hasMissingPackaging) {
      showToastMessage(errorMessage.ORDER_PACKAGE_REQUIRED, 'error');
      return;
    }
    // Packaging split percentage error
    if (hasInvalidPercentage) {
      showToastMessage(errorMessage.ORDER_PACKAGE_PERCENTAGE_INVALID, 'error');
      return;
    }

    const orderDataPayload = CustomerOrderService.getParsedOrderData();
    const existingSpecies =
      orderDataPayload[customerId].species[currentCustomizingId] ?? {};

    orderDataPayload[customerId].species[currentCustomizingId] = {
      ...existingSpecies,
      products: updatedProductCustomizations,
    };
    showToastMessage(responseTexts.PACKAGE_CREATE_SUCCESS, 'success');
    localStorage.setItem('orderData', JSON.stringify(orderDataPayload));
    navigate(FrontendRoutes.SPECIES_SUMMARY);
  };

  return (
    <div className="flex h-screen w-screen bg-white">
      {/* Sidebar */}
      <SideNav active={2} />
      {/* Main Content */}
      <div className="flex-1 overflow-auto relative">
        {/* Header */}
        <ButcherHeader />
        {/* Breadcrumbs */}
        <OrderBreadcrumb
          steps={orderBreadCrumbSteps}
          customerName={`${customerDetails?.firstName} ${customerDetails?.lastName}`}
          activeItem="New Order"
          customerId={customerId}
        />
        <div className="p-3">
          {/* Customer Profile Card */}
          <OrderProfileCard />
          {/* Stepper */}
          <OrderStepper steps={orderStepper} currentStep={5} />
          <div className="p-2 pb-12">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-xl font-semibold p-1">Packaging</h2>
            </div>

            {productCustomizations.length > 0 &&
              productCustomizations.map((product, index) => {
                const master = masterProducts.find(
                  (mp) => mp.id === product.productId,
                );

                const addonsForProduct = product.addOnIds
                  .map((id) => addons.find((addon) => addon.id === id))
                  .filter(
                    (addon): addon is AddonsListProps => addon !== undefined,
                  );

                const packagingList = product.packaging ?? [];

                return (
                  <div
                    key={index}
                    className="bg-[#FFFEFA] border rounded-lg shadow-sm p-4 mt-4"
                  >
                    {/* Product header */}
                    <div className="flex items-center justify-between mb-3">
                      <h1 className="text-lg text-gray-800 font-medium">
                        {master?.name}
                        {product.fatPercent > 0 &&
                          `, ${product.fatPercent}% Fat`}
                      </h1>
                      <div className="flex space-x-3">
                        <div className="font-semibold  bg-[#F5F5F5] text-[#43474E] px-6 py-2 rounded">
                          {product.quantity}{' '}
                          {master?.productType == 1
                            ? `${WeightType.LBS}`
                            : 'Quantity'}
                        </div>
                        {master?.price && (
                          <div className="font-semibold bg-[#F5F5F5] text-[#43474E] px-6 py-2">
                            ${(product.quantity * master?.price).toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="mb-6 flex items-center justify-between px-4 py-2 rounded-md bg-quaternary">
                      <div>
                        <span className="text-gray-600">Price: </span>
                        <span className="text-gray-800 font-semibold">
                          ${master?.price}/
                          {master?.productType === ProductType.QUANTITY
                            ? 'Item'
                            : `${WeightType.LBS}`}
                        </span>
                      </div>
                      <div className="whitespace-nowrap">
                        <span className="text-gray-600 mt-2">
                          Total Estimated weight in Lbs:{' '}
                        </span>
                        <span className="text-gray-800 font-semibold">
                          {(master?.productType === ProductType.QUANTITY
                            ? (product?.quantity ?? 0) * (master?.weight ?? 0)
                            : (product?.quantity ?? 0)
                          ).toFixed(2)}{' '}
                          {WeightType.LBS}
                        </span>
                      </div>
                    </div>
                    {/* Addons */}
                    {addonsForProduct.length > 0 && (
                      <div className="border bg-white px-3 py-3 mb-3">
                        {/* <p className="mb-2">
                          Add-Ons{' '}
                          {master?.upchargeIngredientsFlag === 0 ? ( */}
                        <h2 className="text-xl font-medium text-gray-800 mb-4">
                          Add-ons
                          {master?.upchargeIngredientsFlag === 0 ? (
                            <span className="ml-2">(No Up-Charge)</span>
                          ) : null}
                        </h2>
                        {addonsForProduct.map((addon) => (
                          <div key={addon.id} className="flex justify-between">
                            <span className="text-[#43474E]">{addon.name}</span>
                            <span className="text-[#43474E]">
                              Price:{' '}
                              <span className="font-semibold">
                                {' '}
                                {/* ${addon.price} */}
                                {master?.upchargeIngredientsFlag === 0
                                  ? '$0'
                                  : `$` + `${addon.price?.toFixed(2)}`}
                              </span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Packaging Section */}
                    <h3 className="mb-2 font-medium">Packaging</h3>
                    {packagingList.length > 0 ? (
                      packagingList.map((packaging, pIndex) => {
                        // Find details in masterPackages
                        const packageDetails = masterPackages.find(
                          (mp) => mp.id === packaging.productPackageTypeId,
                        );
                        if (!packageDetails) return null;

                        return (
                          <div
                            key={packaging.productPackageTypeId}
                            className="flex items-center mb-2 justify-between bg-white"
                          >
                            <div className="flex-1 relative">
                              <div
                                className="flex items-center border border-gray-300 rounded px-3 py-1 h-10 text-sm cursor-pointer"
                                onClick={() => {
                                  setActiveCardIndex(index);
                                  setActivePackageIndex(pIndex);
                                  setShowBottomSheet(true);
                                }}
                              >
                                <div className="flex items-center w-full justify-between text-[#1C1B1F] text-lg">
                                  <span className="truncate min-w-[80px] flex-shrink-0">
                                    {packageDetails.packageType}
                                  </span>

                                  <span className="text-center min-w-[80px] flex-shrink-0">
                                    {packageDetails.packageSize}{' '}
                                    {packageDetails.unit}
                                  </span>

                                  <span className="font-medium text-right min-w-[100px] flex-shrink-0">
                                    {`+ $${packageDetails.price.toFixed(2)}`} /
                                    {packageDetails.unit}
                                  </span>

                                  <FontAwesomeIcon
                                    icon={faChevronDown}
                                    className="text-[#A09CAB] text-sm"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Percentage Input */}
                            {packagingList.length > 1 && (
                              <div className="relative w-[80px] ml-2">
                                <input
                                  type="number"
                                  className="h-10 w-full pr-5 pl-2 border border-gray-300 rounded-md focus:outline-primary text-center"
                                  value={packaging.percentage ?? ''}
                                  onChange={(e) =>
                                    handlePercentageChange(
                                      index,
                                      pIndex,
                                      parseFloat(e.target.value),
                                    )
                                  }
                                />
                                <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                                  %
                                </span>
                              </div>
                            )}

                            {/* Delete Button */}
                            <button
                              type="button"
                              onClick={() =>
                                handleRemovePackaging(index, pIndex)
                              }
                              className="mr-2 ml-4 py-1.5 px-2 rounded border text-gray-800"
                            >
                              <FontAwesomeIcon icon={faTrashCan} />
                            </button>
                          </div>
                        );
                      })
                    ) : (
                      <div className="bg-white flex items-center mb-2 justify-between">
                        <div className="flex-1 relative">
                          <div
                            className="flex items-center border border-gray-300 rounded px-3 py-1 h-10 text-sm cursor-pointer"
                            onClick={() => {
                              setActiveCardIndex(index);
                              setActivePackageIndex(null); // Adding new package, not editing existing
                              setShowBottomSheet(true);
                            }}
                          >
                            <div className="flex items-center w-full justify-between">
                              <span className="text-gray-800 truncate min-w-[80px] flex-shrink-0">
                                Select Packaging
                              </span>

                              <span className="text-gray-600 text-center min-w-[80px] flex-shrink-0">
                                —
                              </span>

                              <span className="text-gray-700 text-right min-w-[100px] flex-shrink-0">
                                {/* $0.00 */}
                              </span>

                              <FontAwesomeIcon
                                icon={faChevronDown}
                                className="text-gray-500 text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Add Packaging Button */}
                    {packagingList.length < 2 && (
                      <button
                        type="button"
                        onClick={() => {
                          setActiveCardIndex(index);
                          setShowBottomSheet(true);
                        }}
                        className="w-full text-primary border bg-white border-primary font-medium py-1 rounded mb-4 mt-2"
                      >
                        {packagingList.length > 0
                          ? 'Add More Packaging'
                          : 'Add Packaging'}
                      </button>
                    )}

                    {/* Notes */}
                    <div className="grid grid-cols-1 sm:grid-cols-1">
                      <label
                        htmlFor="notes"
                        className="block font-semibold mb-2"
                      >
                        Notes
                      </label>
                      <textarea
                        name="notes"
                        placeholder="Add notes or description..."
                        value={product.packagingNotes ?? ''}
                        onChange={(e) => {
                          const notes = e.target.value;
                          setProductCustomizations((prev) => {
                            const updated = [...prev];
                            updated[index] = {
                              ...updated[index],
                              packagingNotes: notes,
                            };
                            return updated;
                          });
                        }}
                        className=" block w-full border border-gray-300 rounded-md p-2 mt-1 text-sm focus:outline-primary"
                        rows={3}
                      />
                    </div>
                  </div>
                );
              })}

            {/* Packaging bottom sheet */}
            {showBottomSheet && (
              <PackagingBottomSheet
                onClose={() => {
                  setShowBottomSheet(false);
                  setActiveCardIndex(null);
                  setActivePackageIndex(null);
                }}
                onSelect={handleSelectPackaging}
              />
            )}

            <div className="flex justify-between gap-4 fixed bottom-0 left-14 w-[calc(100%-64px)] p-4">
              <button
                onClick={() =>
                  navigate(FrontendRoutes.CREATE_ORDER_PRODUCT_DETAILS)
                }
                type="button"
                className="w-[50%] px-6 py-2 border border-primary rounded-sm text-sm font-medium text-primary bg-white hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Back
              </button>
              <button
                onClick={handleNext}
                type="submit"
                className="w-[50%] px-6 py-2 border border-transparent rounded-sm text-sm font-medium text-white bg-primary hover:bg-dark-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary flex items-center justify-center space-x-2"
              >
                <span>Next</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
